import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GameController, X } from '@phosphor-icons/react';
import { ManageGamesTab } from './admin/ManageGamesTab';
import { FreeToPlayTab } from './admin/FreeToPlayTab';
import { RawgTab } from './admin/RawgTab';
import { FileImportTab } from './admin/FileImportTab';
import { EditGameDialog } from './admin/EditGameDialog';
import { getToken } from '@/utils/auth';
import { logger } from '@/utils/logger';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPanel = ({ isOpen, onClose, onGamesUpdated }) => {
  const [games, setGames] = useState([]);
  const [editingGame, setEditingGame] = useState(null);

  const fetchGames = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/games`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGames(response.data);
    } catch (error) {
      logger.error('Failed to fetch games:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchGames();
    }
  }, [isOpen, fetchGames]);

  const handleRefresh = useCallback(() => {
    fetchGames();
    onGamesUpdated?.();
  }, [fetchGames, onGamesUpdated]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] border-[#E4E4E7] p-0" data-testid="admin-panel">
        <DialogTitle className="sr-only">Admin Panel - Game Management</DialogTitle>
        <DialogDescription className="sr-only">Manage games for DashLink Gaming</DialogDescription>
        
        <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#0047FF] p-2">
              <GameController size={24} weight="fill" color="#FFFFFF" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-[#09090B]">Admin Panel</h2>
              <p className="text-sm text-[#71717A]">Manage games and imports</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X size={20} weight="bold" />
          </Button>
        </div>

        <Tabs defaultValue="manage" className="flex-1">
          <TabsList className="mx-6 mt-4 bg-[#FAFAFA] border border-[#E4E4E7]">
            <TabsTrigger value="manage">Manage Games</TabsTrigger>
            <TabsTrigger value="import-api">Free-to-Play</TabsTrigger>
            <TabsTrigger value="import-rawg">RAWG.io (500K+)</TabsTrigger>
            <TabsTrigger value="import-file">Import File</TabsTrigger>
          </TabsList>

          <TabsContent value="manage">
            <ManageGamesTab 
              games={games} 
              onRefresh={handleRefresh} 
              onEdit={setEditingGame} 
            />
          </TabsContent>

          <TabsContent value="import-api">
            <FreeToPlayTab onGamesUpdated={handleRefresh} />
          </TabsContent>

          <TabsContent value="import-rawg">
            <RawgTab onGamesUpdated={handleRefresh} />
          </TabsContent>

          <TabsContent value="import-file">
            <FileImportTab onGamesUpdated={handleRefresh} />
          </TabsContent>
        </Tabs>

        {editingGame && (
          <EditGameDialog 
            game={editingGame} 
            onClose={() => setEditingGame(null)} 
            onSaved={handleRefresh}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
